/**
 * Driver bridge helpers — translate run-loop tool calls into postMessage
 * exchanges with the feature-tester-driver script running inside the iframe.
 *
 * The bridge owns iframe navigation and credential substitution; the driver
 * never sees credential values, and `navigate` is intercepted before reaching
 * the driver because page tear-down would race with a result postMessage.
 */
export const NAMESPACE = 'feature-tester';

const HANDSHAKE_TIMEOUT_MS = 15000;
const HANDSHAKE_POLL_INTERVAL_MS = 250;
const TOOL_TIMEOUT_MS = 30000;

// Capture-class tools (html2canvas-backed) can take noticeably longer on
// the very first invocation — html2canvas is loaded on demand and the page
// may still be settling. Bump the timeout for those specifically rather
// than letting all tools share a too-generous limit.
const CAPTURE_TIMEOUT_MS = 45000;
const CAPTURE_TOOLS = new Set(['screenshot', 'capture_evidence']);

/**
 * Send the handshake on a poll until the driver acks or we hit the timeout.
 *
 * Why polling: the iframe's `load` event fires when the document is parsed,
 * but the driver script's message listener may register a few hundred ms
 * later — especially after a 302 chain or when other render-blocking scripts
 * sit ahead of it. A single-shot handshake races with that listener and gets
 * dropped silently. Repeated posts catch the driver as soon as it's ready.
 */
export function handshake(iframe) {

    return new Promise((resolve, reject) => {

        let settled = false;
        let pollHandle = null;
        let timeoutHandle = null;

        const cleanup = () => {

            window.removeEventListener('message', onMessage);
            if (pollHandle) clearInterval(pollHandle);
            if (timeoutHandle) clearTimeout(timeoutHandle);

        };

        const finish = (fn, value) => {

            if (settled) return;
            settled = true;
            cleanup();
            fn(value);

        };

        const onMessage = e => {

            if (e.source !== iframe.contentWindow) return;
            if (!e.data || e.data.namespace !== NAMESPACE) return;
            if (e.data.type === 'handshake_ack') finish(resolve);

        };

        window.addEventListener('message', onMessage);

        const sendHandshake = () => {

            try {

                iframe.contentWindow.postMessage(
                    { namespace: NAMESPACE, type: 'handshake' },
                    '*'
                );

            } catch (e) {

                finish(reject, new Error('iframe inaccessible: ' + e.message));

            }

        };

        sendHandshake();
        pollHandle = setInterval(sendHandshake, HANDSHAKE_POLL_INTERVAL_MS);

        timeoutHandle = setTimeout(() => {

            finish(reject, new Error(
                `handshake timeout after ${HANDSHAKE_TIMEOUT_MS}ms — `
                + `verify the driver script is loaded on the target page and that its origin trusts ${window.location.origin}. `
                + 'For local dev, the script tag needs data-extra-origins="' + window.location.origin + '".'
            ));

        }, HANDSHAKE_TIMEOUT_MS);

    });

}

export function postToDriver(iframe, tool, input) {

    return new Promise((resolve, reject) => {

        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        const onMessage = e => {

            if (e.source !== iframe.contentWindow) return;
            if (!e.data || e.data.namespace !== NAMESPACE) return;
            if (e.data.type !== 'tool_result' || e.data.id !== id) return;

            window.removeEventListener('message', onMessage);

            if (e.data.error)
                reject(new Error(e.data.error));
            else
                resolve(e.data.result);

        };

        window.addEventListener('message', onMessage);

        iframe.contentWindow.postMessage(
            { id, input, namespace: NAMESPACE, tool, type: 'tool' },
            '*'
        );

        setTimeout(() => {

            window.removeEventListener('message', onMessage);
            reject(new Error(`tool '${tool}' timed out in driver`));

        }, CAPTURE_TOOLS.has(tool) ? CAPTURE_TIMEOUT_MS : TOOL_TIMEOUT_MS);

    });

}
